/**
 * Editorial defaults for the search modal — popular destinations and
 * placeholder "ask…" prompts that the modal renders when the input is empty.
 *
 * Popular pages are translated via the `search` namespace (entries shaped
 * `popular.<id>.title|blurb`) and pointed at translation keys instead of
 * hardcoded strings, so each locale picks the right wording. The href list
 * stays here because URLs are locale-agnostic.
 *
 * Prompts are intentionally framed as questions even though v1 has no
 * chatbot — the click-throughs point at /search?q=<prompt> for now and will
 * route to the chat surface once that lands. PostHog `search_prompt_clicked`
 * tells us which prompts get traction so we can seed the bot accordingly.
 */

export interface PopularEntry {
  /** Stable id used as a translation key path (`popular.<id>.title`). */
  id: string
  /** Site-relative path WITHOUT the country/locale prefix. */
  path: string
  /** Renders the "NEW" badge alongside the title. Reserve for genuinely
   *  new surfaces — FAQ / Contact / etc. don't qualify. */
  isNew?: boolean
  /**
   * When set, the empty-state subtitle is a live `From {price}` line from
   * `fetchPrices` (Core package or Advanced blood test) instead of a static
   * translation under `popular.<id>.subtitle`.
   */
  priceFromKey?: "core" | "blood"
}

export const POPULAR_PAGES: PopularEntry[] = [
  { id: "fullBodyMri", path: "/tests/full-body-mri", priceFromKey: "core" },
  {
    id: "advancedBloodTest",
    path: "/tests/advanced-blood-test",
    priceFromKey: "blood",
  },
  { id: "membership", path: "/membership", isNew: true },
  { id: "pricing", path: "/pricing" },
  { id: "howItWorks", path: "/how-it-works" },
  { id: "biomarkers", path: "/biomarkers" },
  { id: "insurance", path: "/insurance" },
  { id: "faq", path: "/faq" },
  { id: "contact", path: "/contact" },
]

export interface PromptEntry {
  /** Stable id used as a translation key (`prompt.<id>`). */
  id: string
}

/**
 * Prompt pills shown in the empty state. The visible pill text — the
 * translation under `search.prompt.<id>` — is also the literal query that
 * gets sent to the engine when the user clicks. Don't add a hidden
 * `query` field that rewrites what the user clicked: it's a leaky
 * abstraction (you typed X, we ran Y) and immediately erodes trust the
 * first time someone notices the URL doesn't match the pill.
 *
 * Result quality is the curator's job: pick prompts that pull useful
 * results through the existing engine. When a prompt returns nothing, the
 * no-results CTA is the safety net.
 */
export const PROMPT_SEEDS: PromptEntry[] = [
  { id: "iron" },
  { id: "heart" },
  { id: "hormones" },
  { id: "cancer" },
]
